import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { createPageUrl } from './utils';
import { useAuth } from './firebase/AuthContext';
import { LanguageProvider, useLanguage } from './components/LanguageContext';
import ChatBot from './components/chat/ChatBot';
import {
  Camera,
  Home,
  Image,
  CreditCard,
  User,
  Menu,
  X,
  Globe,
  Sparkles,
  LogOut,
  Coins,
  Calendar,
  Shield,
  BookOpen,
  HelpCircle,
  Download,
  Settings
} from 'lucide-react';
import { Button } from './components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./components/ui/dropdown-menu";
import { motion, AnimatePresence } from 'framer-motion';
import { Toaster } from 'sonner';

function LayoutContent({ children }) {
  const { language, setLanguage, t, isRTL } = useLanguage();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  // Determine current page name from path
  const path = location.pathname.toLowerCase();
  let currentPageName = 'Home';
  if (path.includes('photobooth')) currentPageName = 'Photobooth';
  else if (path.includes('events')) currentPageName = 'Events';
  else if (path.includes('gallery')) currentPageName = 'Gallery';
  else if (path.includes('pricing')) currentPageName = 'Pricing';
  else if (path.includes('blog')) currentPageName = 'Blog';
  else if (path.includes('faq')) currentPageName = 'FAQ';
  else if (path.includes('download')) currentPageName = 'Download';

  const { user, loading } = useAuth();

  const allNavItems = [
    { name: t('home'), page: 'Home', icon: Home },
    { name: t('photobooth'), page: 'Photobooth', icon: Camera },
    { name: t('gallery'), page: 'Gallery', icon: Image },
    { name: language === 'he' ? 'אירועים' : 'Events', page: 'Events', icon: Calendar },
    { name: language === 'he' ? 'בלוג' : 'Blog', page: 'Blog', icon: BookOpen },
    { name: language === 'he' ? 'שאלות' : 'FAQ', page: 'FAQ', icon: HelpCircle },
    { name: t('pricing'), page: 'Pricing', icon: CreditCard },
    { name: language === 'he' ? 'הורד תוכנה' : 'Download', page: 'Download', icon: Download },
  ];

  // During an active event (in the Photobooth or Events page), minimize distractions
  const navItems = ['Photobooth', 'Events'].includes(currentPageName)
    ? allNavItems.filter(item => ['Home', 'Photobooth', 'Events'].includes(item.page))
    : allNavItems;

  const { logout } = useAuth();
  const handleLogout = () => {
    logout(createPageUrl('Home'));
  };

  return (
    <div className={`min-h-screen bg-background ${isRTL ? 'rtl' : 'ltr'}`}>
      <style>{`
        :root {
          --primary: #3b82f6;
          --primary-light: #60a5fa;
          --primary-dark: #2563eb;
          --secondary: #8b5cf6;
          --secondary-light: #a78bfa;
          --secondary-dark: #7c3aed;
          --accent: #f472b6;
          --bg-gradient: linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%);
          --electric-gradient: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);
        }
        
        .gradient-text {
          background: var(--electric-gradient);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        
        .glass-card {
          background: rgba(30, 41, 59, 0.8);
          backdrop-filter: blur(20px);
          border: 1px solid rgba(51, 65, 85, 0.8);
        }
        
        .glow-blue {
          box-shadow: 0 0 20px rgba(59, 130, 246, 0.5);
        }
        
        .glow-purple {
          box-shadow: 0 0 20px rgba(139, 92, 246, 0.5);
        }
        
        .glow-mixed {
          box-shadow: 0 0 30px rgba(59, 130, 246, 0.3), 0 0 60px rgba(139, 92, 246, 0.2);
        }
        
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        
        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-20px); }
        }
        
        .premium-button {
          background: var(--electric-gradient);
          transition: all 0.3s ease;
        }
        
        .premium-button:hover {
          box-shadow: 0 0 25px rgba(59, 130, 246, 0.5);
          transform: translateY(-1px);
        }
      `}</style>

      {/* Header — use logical inset (start/end) so RTL doesn't clip the navbar on mobile.
          IMPORTANT: no `overflow-hidden` here — it would clip dropdown menus! */}
      <header className="fixed top-0 inset-x-0 z-50 glass-card">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 gap-2">
            {/* Logo */}
            <Link to={createPageUrl('Home')} className="flex items-center gap-2 shrink-0 min-w-0">
              <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-gradient-to-br from-violet-600 to-pink-500 flex items-center justify-center shrink-0">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <span className="hidden sm:inline text-base sm:text-xl font-bold text-white whitespace-nowrap">AI PhotoBooth</span>
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-1 flex-1 justify-center min-w-0 overflow-hidden">
              {navItems.map((item) => (
                <Link
                  key={item.page}
                  to={createPageUrl(item.page)}
                  className={`px-3 lg:px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center gap-2 whitespace-nowrap ${currentPageName === item.page
                      ? 'bg-violet-600/20 text-violet-400'
                      : 'text-slate-400 hover:text-white hover:bg-white/5'
                    }`}
                >
                  <item.icon className="w-4 h-4 shrink-0" />
                  <span className="hidden lg:inline">{item.name}</span>
                </Link>
              ))}
            </nav>

            {/* Right side */}
            <div className="flex items-center gap-1 sm:gap-2 shrink-0">
              {/* Credits */}
              {user && (
                <div className="hidden lg:flex items-center gap-2 px-3 py-1.5 rounded-full bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/30 shrink-0">
                  <Coins className="w-4 h-4 text-amber-400" />
                  <span className="text-sm font-semibold text-amber-400">
                    {user.credits || 0}
                  </span>
                </div>
              )}

              {/* Settings shortcut — always visible on mobile/tablet (iPad) */}
              <Button
                asChild
                variant="ghost"
                size="icon"
                className="text-slate-400 hover:text-white w-11 h-11 shrink-0 min-w-[44px] min-h-[44px]"
                aria-label={language === 'he' ? 'הגדרות' : 'Settings'}
              >
                <Link to={createPageUrl('Dashboard')}>
                  <Settings className="w-5 h-5" />
                </Link>
              </Button>

              {/* Language Switcher */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="text-slate-400 hover:text-white flex items-center gap-1 px-2 sm:px-3 py-2 shrink-0 min-h-[44px] min-w-[44px]">
                    <Globe className="w-5 h-5" />
                    <span className="hidden xl:inline text-sm font-medium">{language === 'he' ? 'עב' : 'EN'}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-slate-900 border-slate-800">
                  <DropdownMenuItem
                    onClick={() => setLanguage('en')}
                    className={language === 'en' ? 'text-violet-400' : 'text-slate-300'}
                  >
                    English
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => setLanguage('he')}
                    className={language === 'he' ? 'text-violet-400' : 'text-slate-300'}
                  >
                    עברית
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* User Menu — login/settings always accessible */}
              {user ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="text-slate-400 hover:text-white w-11 h-11 shrink-0 min-w-[44px] min-h-[44px]">
                      <User className="w-5 h-5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="bg-slate-900 border-slate-800">
                    <DropdownMenuItem asChild>
                      <Link to={createPageUrl('Dashboard')} className="flex items-center gap-2 text-slate-300 min-h-[44px]">
                        <Settings className="w-4 h-4" />
                        {language === 'he' ? 'הגדרות' : 'Settings'}
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to={createPageUrl('Dashboard')} className="flex items-center gap-2 text-slate-300 min-h-[44px]">
                        <User className="w-4 h-4" />
                        {t('dashboard')}
                      </Link>
                    </DropdownMenuItem>
                    {user?.role === 'admin' && (
                      <DropdownMenuItem asChild>
                        <Link to={createPageUrl('Admin')} className="flex items-center gap-2 text-violet-400 min-h-[44px]">
                          <Shield className="w-4 h-4" />
                          {language === 'he' ? 'ניהול' : 'Admin'}
                        </Link>
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem onClick={handleLogout} className="text-red-400 min-h-[44px]">
                      <LogOut className="w-4 h-4 me-2" />
                      {t('logout')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Button
                  onClick={() => window.location.href = createPageUrl('Login')}
                  className="bg-gradient-to-r from-violet-600 to-pink-600 hover:from-violet-700 hover:to-pink-700 text-white w-11 h-11 sm:w-auto sm:px-4 shrink-0 min-w-[44px] min-h-[44px] flex items-center justify-center"
                >
                  <User className="w-5 h-5" />
                  <span className="hidden md:inline ms-2">{language === 'he' ? 'התחבר' : 'Login'}</span>
                </Button>
              )}

              {/* Mobile menu button — 44×44 tap target per iOS HIG */}
              <Button
                variant="ghost"
                size="icon"
                className="md:hidden text-slate-400 w-11 h-11 shrink-0 min-w-[44px] min-h-[44px]"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </Button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden border-t border-slate-800 bg-slate-900/95 backdrop-blur-lg"
            >
              <nav className="px-4 py-6 space-y-2">
                {navItems.map((item) => (
                  <Link
                    key={item.page}
                    to={createPageUrl(item.page)}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center gap-4 px-5 py-4 rounded-xl text-base font-semibold transition-all ${currentPageName === item.page
                        ? 'bg-violet-600 text-white'
                        : 'text-white bg-slate-800/50 hover:bg-slate-700'
                      }`}
                  >
                    <item.icon className="w-6 h-6" />
                    {item.name}
                  </Link>
                ))}
                {user ? (
                  <>
                    <Link
                      to={createPageUrl('Dashboard')}
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-4 px-5 py-4 rounded-xl text-base font-semibold text-white bg-slate-800/50 hover:bg-slate-700"
                    >
                      <User className="w-6 h-6" />
                      {t('dashboard')}
                    </Link>
                    <div className="flex items-center gap-3 px-5 py-4 rounded-xl bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/30">
                      <Coins className="w-6 h-6 text-amber-400" />
                      <span className="text-lg font-bold text-amber-400">{user.credits || 0}</span>
                      <span className="text-amber-400/70">{t('credits')}</span>
                    </div>
                  </>
                ) : (
                  <Link
                    to={createPageUrl('Login')}
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-4 px-5 py-4 rounded-xl text-base font-semibold text-white bg-violet-600 hover:bg-violet-700"
                  >
                    <User className="w-6 h-6" />
                    {language === 'he' ? 'התחבר' : 'Login'}
                  </Link>
                )}
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* Main Content */}
      <main className="pt-16">
        {children}
      </main>

      {/* ChatBot */}
      <ChatBot />

      {/* Footer */}
      <footer className="border-t border-slate-800 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-600 to-pink-500 flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-white" />
              </div>
              <span className="text-lg font-bold text-white">AI PhotoBooth</span>
            </div>

            <div className="flex items-center gap-6 text-sm text-slate-500 flex-wrap justify-center">
              <Link to={createPageUrl('Terms')} className="hover:text-slate-300 transition-colors">{t('termsOfService')}</Link>
              <Link to={createPageUrl('Privacy')} className="hover:text-slate-300 transition-colors">{t('privacyPolicy')}</Link>
              <Link to={createPageUrl('Contact')} className="hover:text-slate-300 transition-colors">{t('contact')}</Link>
              <Link to={createPageUrl('HowItWorks')} className="hover:text-slate-300 transition-colors">{language === 'he' ? 'איך זה עובד' : 'How It Works'}</Link>
              <Link to="/download" className="hover:text-violet-300 transition-colors font-semibold text-violet-400">
                {language === 'he' ? '⬇ הורד שרת הדפסה' : '⬇ Print Server'}
              </Link>
            </div>

            <p className="text-sm text-slate-600">
              2024 AI PhotoBooth. {t('allRightsReserved')}
            </p>
          </div>
        </div>
      </footer>

      {/* Toaster for notifications */}
      <style>{`
        .sonner-toast {
          background: rgba(30, 41, 59, 0.95) !important;
          border: 1px solid rgba(139, 92, 246, 0.3) !important;
          color: white !important;
        }
      `}</style>
      <Toaster richColors />
    </div>
  );
}

export default function Layout({ children }) {
  return (
    <LanguageProvider>
      <LayoutContent children={children} />
    </LanguageProvider>
  );
}